(() => {
  'use strict';

  const desktop = window.matchMedia('(min-width: 990px)');
  const headers = () => [...document.querySelectorAll('[data-qx-header]')];
  const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  const closeDesktopMenus = (header, except = null) => {
    if (!header) return;
    header.querySelectorAll('.qx-header__nav-item').forEach(item => {
      if (item === except) return;
      item.classList.remove('is-key-open');
      item.querySelector('[data-nav-parent]')?.setAttribute('aria-expanded', 'false');
    });
  };

  const resetMobileDetails = header => {
    header?.querySelectorAll('[data-mobile-menu] details[open]').forEach(details => {
      details.open = false;
    });
  };

  const closeMobileMenu = () => {
    const menu = document.querySelector('[data-mobile-menu].is-open');
    if (!menu) return;
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('qx-nav-open');
    document.querySelectorAll('[data-mobile-menu-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
  };

  const closeSearch = () => {
    const drawer = document.querySelector('[data-search-drawer].is-open');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('qx-search-open');
    document.querySelectorAll('[data-search-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
  };

  const closeCart = () => {
    const shell = document.querySelector('[data-qx-cart-shell].is-open');
    if (!shell) return;
    shell.classList.remove('is-open');
    document.body.classList.remove('qx-cart-open');
  };

  const closeWishlist = () => {
    const drawer = document.querySelector('[data-qx-wishlist-drawer]:not([hidden])');
    if (!drawer) return;
    drawer.hidden = true;
    document.body.classList.remove('qx-wishlist-drawer-open');
    document.documentElement.classList.remove('qx-modal-open');
  };

  const prepareOverlayOpen = target => {
    if (!(target instanceof Element)) return;
    if (target.closest('[data-cart-open]')) {
      closeMobileMenu();
      closeSearch();
      closeWishlist();
      return;
    }
    if (target.closest('[data-qx-wishlist-open]')) {
      closeMobileMenu();
      closeSearch();
      closeCart();
      return;
    }
    if (target.closest('[data-search-open]')) {
      closeMobileMenu();
      closeCart();
      closeWishlist();
      return;
    }
    if (target.closest('[data-mobile-menu-open]')) {
      closeSearch();
      closeCart();
      closeWishlist();
    }
  };

  const initHeader = header => {
    if (!header || header.dataset.qxNavControllerReady === 'true') return;
    header.dataset.qxNavControllerReady = 'true';
    header.dataset.qxHeaderReady = 'true';

    let lastScrollY = window.scrollY;
    let scrollFrame = 0;
    const updateHeaderState = () => {
      const y = Math.max(0, window.scrollY);
      header.classList.toggle('is-scrolled', y > 24);
      const movingDown = y > lastScrollY + 5;
      const movingUp = y < lastScrollY - 5;
      const overlayOpen = document.body.classList.contains('qx-search-open') ||
        document.body.classList.contains('qx-nav-open') ||
        document.body.classList.contains('qx-cart-open') ||
        document.body.classList.contains('qx-wishlist-drawer-open');

      if (!desktop.matches || y < 120 || movingUp || overlayOpen || header.matches(':focus-within')) {
        header.classList.remove('is-nav-collapsed');
      } else if (movingDown) {
        header.classList.add('is-nav-collapsed');
      }

      lastScrollY = y;
      scrollFrame = 0;
    };

    updateHeaderState();
    window.addEventListener('scroll', () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateHeaderState);
    }, { passive: true });
    window.addEventListener('resize', () => {
      header.classList.remove('is-nav-collapsed');
      lastScrollY = window.scrollY;
    }, { passive: true });

    header.querySelectorAll('.qx-header__nav-item').forEach(item => {
      const parent = item.querySelector('[data-nav-parent]');
      const dropdown = item.querySelector('[data-nav-dropdown]');
      if (!parent || !dropdown) return;

      let closeTimer = 0;
      const open = () => {
        if (desktop.matches) closeDesktopMenus(header, item);
        window.clearTimeout(closeTimer);
        item.classList.add('is-key-open');
        parent.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        window.clearTimeout(closeTimer);
        item.classList.remove('is-key-open');
        parent.setAttribute('aria-expanded', 'false');
      };

      const previewTriggers = [...dropdown.querySelectorAll('[data-nav-preview-trigger]')];
      const previewPanels = [...dropdown.querySelectorAll('[data-nav-preview-panel]')];
      const setPreview = key => {
        if (!previewPanels.length) return;
        const normalized = String(key);
        previewTriggers.forEach(trigger => trigger.classList.toggle('is-preview-active', trigger.dataset.navPreviewTrigger === normalized));
        previewPanels.forEach(panel => panel.classList.toggle('is-active', panel.dataset.navPreviewPanel === normalized));
      };
      if (previewPanels.length) setPreview(previewPanels[0].dataset.navPreviewPanel || '0');
      previewTriggers.forEach(trigger => {
        const activate = () => setPreview(trigger.dataset.navPreviewTrigger || '0');
        trigger.addEventListener('pointerenter', activate, { passive: true });
        trigger.addEventListener('focusin', activate);
      });

      item.addEventListener('pointerenter', () => {
        if (desktop.matches) open();
      }, { passive: true });
      item.addEventListener('pointerleave', () => {
        if (!desktop.matches) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(close, 90);
      }, { passive: true });
      dropdown.addEventListener('pointerenter', () => {
        if (desktop.matches) window.clearTimeout(closeTimer);
      }, { passive: true });

      parent.addEventListener('keydown', event => {
        if (event.key === ' ') {
          event.preventDefault();
          item.classList.contains('is-key-open') ? close() : open();
          return;
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          open();
          dropdown.querySelector(focusableSelector)?.focus();
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          parent.focus();
        }
      });

      item.addEventListener('focusin', open);
      item.addEventListener('focusout', event => {
        if (!item.contains(event.relatedTarget)) close();
      });
      dropdown.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        close();
        parent.focus();
      });
    });

    const mobileMenu = header.querySelector('[data-mobile-menu]');
    if (mobileMenu && mobileMenu.dataset.qxAccordionReady !== 'true') {
      mobileMenu.dataset.qxAccordionReady = 'true';
      mobileMenu.querySelectorAll('details.qx-mobile-menu__level1, details.qx-mobile-menu__level2').forEach(details => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          const selector = details.classList.contains('qx-mobile-menu__level1')
            ? 'details.qx-mobile-menu__level1'
            : 'details.qx-mobile-menu__level2';
          const scope = details.classList.contains('qx-mobile-menu__level1')
            ? details.parentElement
            : details.closest('.qx-mobile-menu__children');
          (scope || mobileMenu).querySelectorAll(selector).forEach(other => {
            if (other !== details) other.open = false;
          });
        });
      });
    }

    header.addEventListener('click', event => {
      if (event.target.closest('[data-mobile-menu-open]')) resetMobileDetails(header);
      if (event.target.closest('[data-mobile-menu-close]')) {
        window.setTimeout(() => resetMobileDetails(header), 300);
      }
    });
  };

  const init = () => headers().forEach(initHeader);

  document.addEventListener('pointerdown', event => {
    prepareOverlayOpen(event.target);
    if (event.target.closest('.qx-header__nav')) return;
    headers().forEach(header => closeDesktopMenus(header));
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' || event.key === ' ') prepareOverlayOpen(event.target);
    if (event.key !== 'Escape') return;
    headers().forEach(header => closeDesktopMenus(header));
  }, true);

  const onViewportChange = () => {
    headers().forEach(header => {
      closeDesktopMenus(header);
      header.classList.remove('is-nav-collapsed');
      if (desktop.matches) resetMobileDetails(header);
    });
  };

  if (desktop.addEventListener) desktop.addEventListener('change', onViewportChange);
  else desktop.addListener(onViewportChange);

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
  document.addEventListener('DOMContentLoaded', init, { once: true });
  if (document.readyState !== 'loading') init();
})();
